import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { guides } from '@/mocks/guides';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();

  const topRisk = guides.filter((g) => g.risk === 'high').slice(0, 3);

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = keyword.trim();
    navigate(trimmed ? `/guides?q=${encodeURIComponent(trimmed)}` : '/guides');
  };

  return (
    <section className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20editorial%20desk%20scene%20with%20receipts%2C%20magnifier%20lens%20and%20a%20laptop%20showing%20a%20price%20chart%2C%20amber%20and%20brown%20tones%2C%20soft%20directional%20light%2C%20cinematic%20minimal%20composition%20with%20deep%20shadows&width=1600&height=900&seq=hero-kanliao-main&orientation=landscape"
          alt="看料避雷社 消费避雷指南 编辑台场景"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/75 to-foreground-950/40"></div>
      </div>

      <div className="relative w-full px-4 md:px-8 py-14 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 text-background-50">
            <span className="inline-flex items-center gap-2 rounded-full border border-primary-300/60 bg-primary-500/20 px-3 py-1 text-xs font-label text-primary-100">
              <i className="ri-radar-line"></i>
              消费避雷 · 持续更新
            </span>
            <h1 className="mt-5 font-heading text-3xl md:text-5xl lg:text-6xl font-black leading-[1.15] text-background-50">
              入手之前，
              <br />
              先看这一眼。
            </h1>
            <p className="mt-5 text-sm md:text-base leading-relaxed text-background-100/90 max-w-2xl font-body">
              把网购、美妆、数码、食饮、旅宿、教育、理财、家装里反复出现的坑点，整理成一份份可以照着做的避雷清单。
              哪些别踩、哪些当心、哪些要先截图留证，一次说清楚。
            </p>

            <form onSubmit={handleSearch} className="mt-7 max-w-xl flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"></i>
                <input
                  type="search"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索：预售定金、代购、延保、民宿照骗…"
                  className="w-full rounded-md border border-background-100/20 bg-background-50/95 pl-11 pr-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 font-label"
                  aria-label="搜索避雷清单"
                />
              </div>
              <button
                type="submit"
                className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-label text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-search-2-line"></i>
                搜清单
              </button>
            </form>

            <div className="mt-8 flex flex-wrap gap-x-8 gap-y-4">
              <div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-background-50">120+</div>
                <div className="text-xs text-background-200/80 font-label mt-1">收录避雷清单</div>
              </div>
              <div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-background-50">{categories.length} 类</div>
                <div className="text-xs text-background-200/80 font-label mt-1">覆盖消费场景</div>
              </div>
              <div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-background-50">每周</div>
                <div className="text-xs text-background-200/80 font-label mt-1">复核与更新</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="rounded-lg border border-background-100/20 bg-background-50/95 backdrop-blur p-4 md:p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-heading text-base font-bold text-foreground-950 flex items-center gap-2">
                  <span className="w-7 h-7 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                    <i className="ri-alarm-warning-line"></i>
                  </span>
                  本周高风险提示
                </h2>
                <span className="text-xs font-label text-foreground-500">按关注度排序</span>
              </div>
              <ul className="flex flex-col divide-y divide-background-200">
                {topRisk.map((guide, index) => (
                  <li key={guide.slug}>
                    <Link to={`/guides/${guide.slug}`} className="group flex items-start gap-3 py-3">
                      <span className="font-heading text-lg font-bold text-accent-500/70 w-6 shrink-0">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                      <span className="flex-1">
                        <span className="block font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-700 transition-colors leading-snug">
                          {guide.title}
                        </span>
                        <span className="mt-1 flex items-center gap-3 text-xs text-foreground-500 font-label">
                          <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{guide.views}</span>
                          <span className="flex items-center gap-1"><i className="ri-time-line"></i>{guide.readTime}</span>
                        </span>
                      </span>
                      <i className="ri-arrow-right-up-line text-foreground-400 group-hover:text-primary-600 transition-colors mt-1"></i>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/guides"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md border border-primary-300 px-4 py-2.5 text-sm font-label text-primary-700 hover:bg-primary-50 transition-colors"
              >
                浏览全部避雷清单
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}